import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Building2, Palette, CreditCard, ToggleLeft, Briefcase } from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { brandingService } from '@/features/branding/services/branding.service';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/_authenticated/minha-empresa')({
  component: MinhaEmpresaPage,
});

function MinhaEmpresaPage() {
  return (
    <PermissionGate permission={Permission.ADMIN_TENANT}>
      <MinhaEmpresaContent />
    </PermissionGate>
  );
}

function MinhaEmpresaContent() {
  const { data: branding, isLoading } = useQuery({
    queryKey: ['branding', 'me'],
    queryFn: () => brandingService.getMyBranding(),
  });

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold">Minha Empresa</h1>
        <p className="text-muted-foreground mt-1">
          Dados e configurações do tenant (organização que usa o Asseto)
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Dados do tenant
          </CardTitle>
          <CardDescription>
            Identidade da organização no Asseto. CNPJ e PIX de prestadores ficam em Meu Perfil.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-64" />
              <Skeleton className="h-5 w-56" />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Nome do app</p>
                <p className="text-lg">{branding?.appName || '—'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Slug</p>
                <p className="font-mono text-sm">{branding?.slug || '—'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Domínio customizado</p>
                <p className="text-lg">{branding?.customDomain || '—'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Cor primária</p>
                <div className="flex items-center gap-2 mt-1">
                  {branding?.primaryColor ? (
                    <>
                      <span
                        className="inline-block h-5 w-5 rounded border"
                        style={{ backgroundColor: branding.primaryColor }}
                      />
                      <span className="font-mono text-sm">{branding.primaryColor}</span>
                    </>
                  ) : (
                    <span>—</span>
                  )}
                </div>
              </div>
              {branding?.logoUrl && (
                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Logo</p>
                  <img
                    src={branding.logoUrl}
                    alt="Logo do tenant"
                    className="h-12 object-contain"
                  />
                </div>
              )}
            </div>
          )}
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/branding">Editar identidade visual</Link>
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/admin/branding"
          className="flex items-center gap-3 rounded-lg border p-4 hover:bg-muted/50 transition-colors"
        >
          <Palette size={20} className="text-muted-foreground" />
          <div>
            <p className="font-medium text-sm">Identidade Visual</p>
            <p className="text-xs text-muted-foreground">Logo, cores e domínio</p>
          </div>
        </Link>

        <Link
          to="/companies"
          className="flex items-center gap-3 rounded-lg border p-4 hover:bg-muted/50 transition-colors"
        >
          <Briefcase size={20} className="text-muted-foreground" />
          <div>
            <p className="font-medium text-sm">Empresas do tenant</p>
            <p className="text-xs text-muted-foreground">CNPJ e empresas pagantes</p>
          </div>
        </Link>

        <Link
          to="/admin/subscription"
          className="flex items-center gap-3 rounded-lg border p-4 hover:bg-muted/50 transition-colors"
        >
          <CreditCard size={20} className="text-muted-foreground" />
          <div>
            <p className="font-medium text-sm">Assinatura</p>
            <p className="text-xs text-muted-foreground">Plano e add-ons ativos</p>
          </div>
        </Link>

        <Link
          to="/admin/features"
          className="flex items-center gap-3 rounded-lg border p-4 hover:bg-muted/50 transition-colors"
        >
          <ToggleLeft size={20} className="text-muted-foreground" />
          <div>
            <p className="font-medium text-sm">Módulos Ativos</p>
            <p className="text-xs text-muted-foreground">Feature flags e add-ons</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
